import { useState, useEffect } from 'react';
import { useHabits } from '../hooks/useHabits';
import { useDragSort } from '../hooks/useDragSort';
import { Spinner, EmptyState, Modal, Input, Button, OfflineBadge } from '../components/ui';

const WEEKDAYS_SHORT = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const EMOJI_OPTIONS  = ['⭐','🏃','💧','📚','🧘','💪','🥗','😴','🎯','✍️','🎸','🌿','💊','🧹','🐕','🧠','🌅','🚴'];
const COLOR_OPTIONS  = [
  { label: 'Teal',   value: 'var(--accent)' },
  { label: 'Green',  value: '#16A34A'       },
  { label: 'Blue',   value: '#2563EB'       },
  { label: 'Purple', value: '#7C3AED'       },
  { label: 'Pink',   value: '#DB2777'       },
  { label: 'Orange', value: '#EA580C'       },
  { label: 'Amber',  value: '#D97706'       },
  { label: 'Rose',   value: '#E11D48'       },
];

function PlusIcon() {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>;
}
function FlameIcon({ color }) {
  return <svg width="11" height="11" viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="1" strokeLinecap="round"><path d="M12 2c0 0-4 4-4 8a4 4 0 008 0c0-2-1-4-1-4s-1 2-3 2c-1 0-2-1-2-2 0-2 2-4 2-4z"/></svg>;
}

function HabitModal({ open, onClose, onSave, habit }) {
  const isEdit    = !!habit;
  const blankForm = { name: '', emoji: '⭐', color: 'var(--accent)', target_days: [0,1,2,3,4,5,6] };
  const [form,   setForm]   = useState(blankForm);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  useEffect(() => {
    if (open) {
      setForm(habit
        ? { name: habit.name, emoji: habit.emoji || '⭐', color: habit.color || 'var(--accent)', target_days: habit.target_days || [0,1,2,3,4,5,6] }
        : blankForm
      );
      setError('');
      setSaving(false);
    }
  }, [open, habit?.id]);

  const set       = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleDay = (d) => {
    const next = form.target_days.includes(d)
      ? form.target_days.filter(x => x !== d)
      : [...form.target_days, d].sort((a,b) => a-b);
    if (next.length === 0) return;
    set('target_days', next);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Habit name is required'); return; }
    setSaving(true);
    try { await onSave(form); onClose(); }
    catch { setError('Failed to save. Please try again.'); setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? 'Edit habit' : 'New habit'}>
      <div style={{ display:'flex', flexDirection:'column', gap: 16 }}>
        <Input placeholder="Habit name (e.g. Drink water)" value={form.name}
          onChange={e => { set('name', e.target.value); setError(''); }} error={error} autoFocus/>

        <div>
          <p style={{ fontSize: 11, fontWeight: 600, color:'var(--text3)', marginBottom: 8, textTransform:'uppercase', letterSpacing:'0.05em' }}>Icon</p>
          <div style={{ display:'flex', flexWrap:'wrap', gap: 6 }}>
            {EMOJI_OPTIONS.map(e => (
              <button key={e} onClick={() => set('emoji', e)} style={{ width: 38, height: 38, borderRadius: 9, border:`2px solid ${form.emoji === e ? 'var(--accent)' : 'var(--border)'}`, background: form.emoji === e ? 'var(--accent-light)' : 'var(--surface)', cursor:'pointer', fontSize: 17, transition:'all 0.1s', display:'flex', alignItems:'center', justifyContent:'center' }}>{e}</button>
            ))}
          </div>
        </div>

        <div>
          <p style={{ fontSize: 11, fontWeight: 600, color:'var(--text3)', marginBottom: 8, textTransform:'uppercase', letterSpacing:'0.05em' }}>Color</p>
          <div style={{ display:'flex', gap: 8, flexWrap:'wrap' }}>
            {COLOR_OPTIONS.map(c => (
              <button key={c.value} onClick={() => set('color', c.value)} title={c.label} style={{ width: 28, height: 28, borderRadius:'50%', background: c.value === 'var(--accent)' ? 'var(--accent)' : c.value, border: form.color === c.value ? '3px solid var(--text)' : '2px solid transparent', cursor:'pointer', outline:'none', boxShadow: form.color === c.value ? '0 0 0 2px var(--bg)' : 'none', transition:'all 0.1s' }}/>
            ))}
          </div>
        </div>

        <div>
          <p style={{ fontSize: 11, fontWeight: 600, color:'var(--text3)', marginBottom: 8, textTransform:'uppercase', letterSpacing:'0.05em' }}>Track on</p>
          <div style={{ display:'flex', gap: 6, flexWrap:'wrap' }}>
            {[1,2,3,4,5,6,0].map(d => {
              const selected = form.target_days.includes(d);
              const col      = form.color === 'var(--accent)' ? 'var(--accent)' : form.color;
              return (
                <button key={d} onClick={() => toggleDay(d)} style={{ width: 38, height: 38, borderRadius:'50%', border:'none', background: selected ? col : 'var(--bg)', color: selected ? 'white' : 'var(--text3)', outline: selected ? 'none' : '1.5px solid var(--border)', fontSize: 11, fontWeight: 600, cursor:'pointer', fontFamily:'inherit', transition:'all 0.15s', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {WEEKDAYS_SHORT[d]}
                </button>
              );
            })}
          </div>
          <p style={{ fontSize: 11, color:'var(--text3)', marginTop: 6 }}>
            {form.target_days.length === 7 ? 'Every day'
              : form.target_days.length === 5 && !form.target_days.includes(0) && !form.target_days.includes(6) ? 'Weekdays only'
              : form.target_days.length === 2 && form.target_days.includes(0) && form.target_days.includes(6) ? 'Weekends only'
              : form.target_days.length === 1 ? `Every ${['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][form.target_days[0]]}`
              : `${form.target_days.length} days per week`}
          </p>
        </div>

        <div style={{ display:'flex', gap: 8, justifyContent:'flex-end', paddingTop: 10, borderTop:'1px solid var(--border)' }}>
          <Button variant="ghost" onClick={onClose} size="sm">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} size="sm">{saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add habit'}</Button>
        </div>
      </div>
    </Modal>
  );
}

function DayCell({ day, color, onToggle, size = 30 }) {
  const now      = new Date();
  const todayMs  = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const parts    = (day.date || '').split('-');
  const cellMs   = new Date(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2])).getTime();
  const isToday  = cellMs === todayMs;
  const isFuture = cellMs > todayMs;
  const col      = color === 'var(--accent)' ? 'var(--accent)' : color;

  if (!day.scheduled) return <div style={{ width: size, height: size, flexShrink: 0 }}/>;
  if (isFuture) return <div title={day.date} style={{ width: size, height: size, flexShrink: 0, borderRadius: Math.round(size*0.27), border:'1.5px dashed var(--border)', opacity: 0.4 }}/>;

  return (
    <button onClick={() => onToggle(day.date)} title={day.date} style={{
      width: size, height: size, flexShrink: 0,
      borderRadius: Math.round(size*0.27),
      border: day.done ? 'none' : isToday ? `2px solid ${col}` : '1.5px solid var(--border)',
      background: day.done ? col : 'transparent',
      cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', transition:'all 0.15s',
    }}>
      {day.done && <svg width={size*0.4} height={size*0.32} viewBox="0 0 11 9" fill="none"><path d="M1 4.5L4 7.5L10 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
    </button>
  );
}

function RateBar({ week, color }) {
  const scheduled = (week || []).filter(d => d.scheduled);
  const done      = (week || []).filter(d => d.done);
  const pct       = scheduled.length > 0 ? Math.round(done.length / scheduled.length * 100) : 0;
  const col       = color === 'var(--accent)' ? 'var(--accent)' : color;
  return (
    <div style={{ display:'flex', alignItems:'center', gap: 5, marginTop: 3 }}>
      <div style={{ flex: 1, height: 3, borderRadius: 2, background:'var(--border)', overflow:'hidden' }}>
        <div style={{ height:'100%', borderRadius: 2, width:`${pct}%`, background: col, transition:'width 0.4s ease' }}/>
      </div>
      <span style={{ fontSize: 10, color:'var(--text3)', fontWeight: 600, flexShrink: 0 }}>{pct}%</span>
    </div>
  );
}

function DayHeaders({ week, cellSize }) {
  if (!week?.length) return null;
  const today = new Date(); today.setHours(0,0,0,0);
  return (
    <div style={{ display:'flex', alignItems:'center', gap: 8, padding:'8px 12px 4px', borderBottom:'1px solid var(--border)' }}>
      <div style={{ width: 32, height: 32, flexShrink: 0 }}/>
      <div style={{ flex: 1, minWidth: 0 }}/>
      <div style={{ display:'flex', gap: 4, flexShrink: 0 }}>
        {week.map(day => {
          const d       = new Date(day.date + 'T00:00:00');
          const isToday = d.setHours(0,0,0,0) === today.getTime();
          return (
            <div key={day.date} style={{ width: cellSize, flexShrink: 0, textAlign:'center' }}>
              <div style={{ fontSize: 9, fontWeight: 700, color: isToday ? 'var(--accent)' : 'var(--text3)', textTransform:'uppercase', letterSpacing:'0.03em' }}>{WEEKDAYS_SHORT[new Date(day.date+'T00:00:00').getDay()]}</div>
              <div style={{ fontSize: 9, fontWeight: isToday ? 700 : 400, color: isToday ? 'var(--accent)' : 'var(--text3)' }}>{new Date(day.date+'T00:00:00').getDate()}</div>
            </div>
          );
        })}
      </div>
      <div style={{ width: 58, flexShrink: 0 }}/>
    </div>
  );
}

function DragHandle() {
  return (
    <svg width="10" height="14" viewBox="0 0 10 14" fill="none" style={{ flexShrink: 0 }}>
      <circle cx="3" cy="3"  r="1.3" fill="var(--text3)"/>
      <circle cx="7" cy="3"  r="1.3" fill="var(--text3)"/>
      <circle cx="3" cy="7"  r="1.3" fill="var(--text3)"/>
      <circle cx="7" cy="7"  r="1.3" fill="var(--text3)"/>
      <circle cx="3" cy="11" r="1.3" fill="var(--text3)"/>
      <circle cx="7" cy="11" r="1.3" fill="var(--text3)"/>
    </svg>
  );
}

function HabitRow({ habit, onToggle, onEdit, onDelete, cellSize, rowProps, handleProps }) {
  const [hovering, setHovering] = useState(false);
  const color = habit.color || 'var(--accent)';
  return (
    <div
      {...(rowProps || {})}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      style={{
        display:'flex', alignItems:'center', gap: 8,
        padding:'10px 12px',
        borderBottom:'1px solid var(--divider)',
        background: hovering ? 'var(--hover)' : 'transparent',
        transition:'background 0.1s',
      }}
    >
      {/* Drag handle — ONLY this has draggable=true */}
      <div
        {...(handleProps || {})}
        title="Drag to reorder"
        style={{ opacity: hovering ? 0.45 : 0, transition:'opacity 0.15s', flexShrink: 0, display:'flex', alignItems:'center', cursor:'grab', color:'var(--text3)', userSelect:'none' }}
      >
        <DragHandle />
      </div>
      <div style={{ width: 32, height: 32, borderRadius: 9, flexShrink: 0, background: color === 'var(--accent)' ? 'var(--accent-light)' : `${color}18`, display:'flex', alignItems:'center', justifyContent:'center', fontSize: 15 }}>{habit.emoji}</div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display:'flex', alignItems:'center', gap: 5 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex: 1, minWidth: 0 }}>{habit.name}</div>
          {habit.streak > 0 && (
            <span style={{ display:'inline-flex', alignItems:'center', gap: 2, fontSize: 10, fontWeight: 700, color: color === 'var(--accent)' ? 'var(--accent)' : color, flexShrink: 0 }}>
              <FlameIcon color={color === 'var(--accent)' ? 'var(--accent)' : color}/>{habit.streak}
            </span>
          )}
        </div>
        <RateBar week={habit.week || []} color={color}/>
      </div>

      <div style={{ display:'flex', gap: 4, flexShrink: 0 }}>
        {(habit.week || []).map(day => (
          <DayCell key={day.date} day={day} color={color} onToggle={date => onToggle(habit.id, date)} size={cellSize}/>
        ))}
      </div>

      <div style={{ width: 58, flexShrink: 0, display:'flex', alignItems:'center', justifyContent:'flex-end', gap: 2 }}>
        <button onClick={() => onEdit(habit)} title="Edit" style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', padding: 4, borderRadius: 5, display:'flex', width: 28, height: 28, alignItems:'center', justifyContent:'center', flexShrink: 0, opacity: hovering ? 1 : 0, transition:'opacity 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.color='var(--text)'} onMouseLeave={e => e.currentTarget.style.color='var(--text3)'}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
        </button>
        <button onClick={() => onDelete(habit.id)} title="Delete" style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', padding: 4, borderRadius: 5, display:'flex', width: 28, height: 28, alignItems:'center', justifyContent:'center', flexShrink: 0, opacity: hovering ? 1 : 0, transition:'opacity 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.color='#EF4444'} onMouseLeave={e => e.currentTarget.style.color='var(--text3)'}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
        </button>
      </div>
    </div>
  );
}


const DRAG_CSS = `
  [data-dragging="true"] { opacity: 0.4 !important; cursor: grabbing !important; }
  [data-dragover="true"]  { outline: 2px solid var(--accent) !important; outline-offset: -2px; }
`;
export default function HabitsPage() {
  const { habits, loading, isOnline, addHabit, updateHabit, removeHabit, toggleHabit, reorderHabits } = useHabits();

  // Drag-to-reorder
  const { items: sortedHabits, rowProps, handleProps } = useDragSort(habits, reorderHabits);

  const [modalOpen, setModalOpen] = useState(false);
  const [editHabit, setEditHabit] = useState(null);
  const [cellSize,  setCellSize]  = useState(28);

  useEffect(() => {
    const update = () => {
      const available = window.innerWidth - 48 - 32 - 120 - 58 - 40;
      setCellSize(Math.max(20, Math.min(30, Math.floor(available / 7))));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const handleOpenAdd  = () => { setEditHabit(null);  setModalOpen(true); };
  const handleOpenEdit = (h) => { setEditHabit(h);    setModalOpen(true); };
  const handleClose    = () => { setModalOpen(false); setEditHabit(null); };
  const handleSave     = async (data) => {
    if (editHabit) await updateHabit(editHabit.id, data);
    else           await addHabit(data);
  };
  const handleDelete   = async (id) => {
    if (!window.confirm('Delete this habit? All history will be lost.')) return;
    await removeHabit(id);
  };

  const now      = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
  const scheduled = habits.filter(h => (h.week || []).some(d => d.date === todayStr && d.scheduled));
  const done      = habits.filter(h => (h.week || []).some(d => d.date === todayStr && d.done));
  const sampleWeek = sortedHabits[0]?.week || [];

  if (loading) {
    return <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}><Spinner size={28}/></div>;
  }

  return (
    <div style={{ maxWidth: 720, margin:'0 auto', padding:'clamp(16px,4vw,32px) clamp(12px,4vw,24px) 100px' }}>
      <style>{DRAG_CSS}</style>

      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom: 20, flexWrap:'wrap', gap: 12 }}>
        <div>
          <div style={{ display:'flex', alignItems:'center', gap: 8, marginBottom: 2 }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, color:'var(--text)', letterSpacing:'-0.5px' }}>Habits</h1>
            {!isOnline && <OfflineBadge />}
          </div>
          <p style={{ fontSize: 13, color:'var(--text3)' }}>
            {scheduled.length === 0 ? 'No habits scheduled for today'
              : done.length === scheduled.length ? `All ${scheduled.length} done today 🎉`
              : `${scheduled.length - done.length} of ${scheduled.length} remaining today`}
          </p>
        </div>
        <button onClick={handleOpenAdd} style={{ display:'flex', alignItems:'center', gap: 5, padding:'8px 14px', borderRadius: 8, background:'var(--brand-grad)', border:'none', color:'white', fontWeight: 600, fontSize: 13, cursor:'pointer', fontFamily:'inherit', flexShrink: 0, minHeight: 38 }}
          onMouseEnter={e => e.currentTarget.style.filter='brightness(1.08)'}
          onMouseLeave={e => e.currentTarget.style.filter=''}
        ><PlusIcon /> Add habit</button>
      </div>

      {scheduled.length > 0 && (
        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius: 12, padding:'14px 16px', marginBottom: 18, display:'flex', alignItems:'center', gap: 14 }}>
          <div style={{ position:'relative', width: 44, height: 44, flexShrink: 0 }}>
            <svg width="44" height="44" viewBox="0 0 44 44" style={{ transform:'rotate(-90deg)' }}>
              <circle cx="22" cy="22" r="16" fill="none" stroke="var(--border)" strokeWidth="4"/>
              <circle cx="22" cy="22" r="16" fill="none" stroke="var(--accent)" strokeWidth="4" strokeLinecap="round"
                strokeDasharray={`${done.length/scheduled.length*2*Math.PI*16} ${2*Math.PI*16}`}
                style={{ transition:'stroke-dasharray 0.5s ease' }}/>
            </svg>
            <div style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', fontSize: 10, fontWeight: 700, color:'var(--text)' }}>{done.length}/{scheduled.length}</div>
          </div>
          <div>
            <p style={{ fontSize: 13, fontWeight: 600, color:'var(--text)', marginBottom: 2 }}>
              {done.length === scheduled.length ? 'All habits done for today! 🎉' : `${scheduled.length - done.length} of ${scheduled.length} remaining`}
            </p>
            <p style={{ fontSize: 12, color:'var(--text3)' }}>{now.toLocaleDateString('en-US', { weekday:'long', month:'long', day:'numeric' })}</p>
          </div>
        </div>
      )}

      {habits.length > 0 ? (
        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius: 12, overflow:'hidden' }}>
          <DayHeaders week={sampleWeek} cellSize={cellSize}/>
          {sortedHabits.map(h => (
            <HabitRow
              key={h.id}
              habit={h}
              onToggle={toggleHabit}
              onEdit={handleOpenEdit}
              onDelete={handleDelete}
              cellSize={cellSize}
              rowProps={rowProps(h.id)}
              handleProps={handleProps(h.id)}
            />
          ))}
        </div>
      ) : (
        <EmptyState icon="🔥" title="No habits yet" sub="Add your first habit to start building streaks."/>
      )}

      {!isOnline && habits.length > 0 && (
        <p style={{ marginTop: 12, fontSize: 12, color:'var(--text3)', textAlign:'center' }}>Habits saved locally. Sign in to sync across devices.</p>
      )}

      <HabitModal open={modalOpen} onClose={handleClose} onSave={handleSave} habit={editHabit}/>
    </div>
  );
}